'use client';

import { lucideIcons } from '@/lib/lucideIcons';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Servicio } from '@/components/hooks/useServicios';
import { X } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  servicio: Servicio | null;
}

function LucideIcon({ name, className }: { name: string; className?: string }) {
  const Icon = (lucideIcons as any)[name];
  if (!Icon) return null;
  return <Icon className={className ?? 'w-5 h-5'} />;
}

const formatDuration = (min?: number): string => {
  if (!min) return '-';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return [h > 0 ? `${h}h` : '', m > 0 ? `${m}min` : ''].join(' ').trim() || '-';
};

const formatDias = (servicio: Servicio): string => {
  const activos = servicio.dias_servicio?.filter((d) => d.activo) || [];
  if (!activos.length) return '';
  return activos
    .map((d) => d.dia_semana.charAt(0).toUpperCase() + d.dia_semana.slice(1))
    .join(', ');
};

const formatHorarios = (servicio: Servicio): string => {
  const activos = servicio.horarios_servicio?.filter((h) => h.activo) || [];
  if (!activos.length) return '';
  return activos.map((h) => h.hora.slice(0, 5)).join(', ');
};

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-1.5 text-xs min-h-[20px]">
      <span className="text-gray-400 dark:text-gray-500 w-28 shrink-0">
        {label}:
      </span>
      <span className="font-medium text-gray-800 dark:text-white break-words flex-1">
        {value}
      </span>
    </div>
  );
}

export const ServiceDetailsDialog: React.FC<Props> = ({
  open,
  onClose,
  servicio,
}) => {
  if (!servicio) return null;

  const diasStr = formatDias(servicio);
  const horariosStr = formatHorarios(servicio);

  const clientesValue = (() => {
    const min = servicio.cliente_min;
    const max = servicio.cliente_max;
    if (min != null && max != null) return `mín. ${min} — máx. ${max}`;
    if (min != null) return `mín. ${min}`;
    if (max != null) return `máx. ${max}`;
    return null;
  })();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-md sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
        "
      >
        {/* TopBar */}
        <div className="flex items-center gap-3 px-4 pt-3 pb-2 shrink-0">
          <div className="h-1 flex-1 rounded-full bg-[#103254] dark:bg-blue-400" />
          <button
            onClick={onClose}
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Header */}
        <div className="px-5 pb-1 shrink-0">
          <div className="flex items-center gap-2">
            {servicio.icono && (
              <div className="w-8 h-8 rounded-lg bg-[#103254] dark:bg-blue-500 flex items-center justify-center shrink-0">
                <LucideIcon
                  name={servicio.icono}
                  className="w-4 h-4 text-white"
                />
              </div>
            )}
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400">
                {servicio.servicio}
              </h2>
              {servicio.categoria_servicio?.nombre && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                  {servicio.categoria_servicio.nombre}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Detalles */}
        <div className="px-5 py-3 space-y-1.5 flex-1 overflow-y-auto">
          <div className="rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 p-3 space-y-2">
            {servicio.descripcion && (
              <Row label="Descripción" value={servicio.descripcion} />
            )}
            {servicio.precio != null && (
              <Row
                label="Precio"
                value={`${servicio.precio.toFixed(2)} €${
                  servicio.precio_aplica ? ` (${servicio.precio_aplica})` : ''
                }`}
              />
            )}
            {servicio.duracion_minutos != null && (
              <Row
                label="Duración"
                value={formatDuration(servicio.duracion_minutos)}
              />
            )}
            {clientesValue && <Row label="Clientes" value={clientesValue} />}
            <Row
              label="Grupos"
              value={
                <span
                  className={
                    servicio.permite_grupos
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-500 dark:text-red-400'
                  }
                >
                  {servicio.permite_grupos ? 'Sí' : 'No'}
                </span>
              }
            />
            <Row
              label="Trabajador"
              value={
                <span
                  className={
                    servicio.requiere_trabajador
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-gray-500 dark:text-gray-400'
                  }
                >
                  {servicio.requiere_trabajador ? 'Requerido' : 'No requerido'}
                </span>
              }
            />
            {diasStr && <Row label="Días" value={diasStr} />}
            {horariosStr && <Row label="Horarios" value={horariosStr} />}
          </div>

          {(servicio.fases_servicio?.length ?? 0) > 0 && (
            <div className="rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 p-3 space-y-2">
              <p className="text-xs font-semibold text-[#103254] dark:text-blue-400">
                Fases
              </p>
              {servicio.fases_servicio!.map((f, i) => (
                <div
                  key={f.id_fase}
                  className="flex items-center justify-between gap-2 text-xs"
                >
                  <span className="text-gray-700 dark:text-gray-300">
                    <span className="font-medium text-[#103254] dark:text-blue-400 mr-1">
                      {i + 1}.
                    </span>
                    {f.nombre_fase}
                    {f.requiere_atencion && (
                      <span className="ml-1.5 text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-md">
                        atención
                      </span>
                    )}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500 shrink-0">
                    {f.duracion_minutos}min
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};